/**
 * Route guard for staff-only admin surfaces (moderation queue).
 *
 * Mirror of {@link SelectorOnlyRoute}: sits inside `<ProtectedRoute>`, so the
 * user is authenticated by the time this runs. Layered RBAC:
 *
 *  - role === 'admin' / 'super_admin' → render children.
 *  - any other role (selector / client_*) → redirect to `/`, where
 *    `<RoleHomeRedirect>` dispatches them to their own landing page.
 *
 * `user` can be momentarily `null` right after silent-refresh resolves — return
 * `null` until the auth context settles (the outer `<ProtectedRoute>` already
 * renders a loader during bootstrap so there is no longer than one-render flash).
 */

import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/use-auth';

export function isStaff(role: string | undefined): boolean {
  return role === 'admin' || role === 'super_admin';
}

export function StaffOnlyRoute({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  if (!user) return null;
  if (!isStaff(user.role)) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}
